var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
/**
 * 解析默认属性
 * @hidden
 */
export var DEFAULT_PARSE_OPTIONS = {
    decode: true,
    parseArray: true,
};
/**
 * 解析 queryParam 字符串
 *
 * ```js
 * FastUtils.parseQueryParam('?a=1&b=2'); // => {a: "1", b: "2"}
 * FastUtils.parseQueryParam('a=1&b=2'); // => {a: "1", b: "2"}
 *
 * // 处理多个同名参数
 * FastUtils.parseQueryParam('?a=1&b=2&b=3'); // => {a: "1", b: ["2", "3"]}
 * FastUtils.parseQueryParam('?a=1&b=2&b=3', { parseArray: false }); // => {a: "1", b: "2"}
 *
 * // 其他特殊情况
 * FastUtils.parseQueryParam('?a'); // => {a: null}
 * FastUtils.parseQueryParam('?a='); // => {a: ""}
 * FastUtils.parseQueryParam(''); // => {}
 * ```
 *
 * @category UrlQuery
 * @param input queryParam 输入
 * @param options
 * - `decode`：默认为 `true`
 * - `parseArray`：默认为 `true`
 */
function parseQueryParam(input, options) {
    var result = {};
    options = __assign(__assign({}, DEFAULT_PARSE_OPTIONS), options);
    if (typeof input !== 'string') {
        return result;
    }
    input = input.trim().replace(/^[?#&]/, '');
    if (!input) {
        return result;
    }
    for (var _i = 0, _a = input.split('&'); _i < _a.length; _i++) {
        var param = _a[_i];
        var _b = param.split('='), key = _b[0], value = _b[1];
        if (value === undefined) {
            value = null;
        }
        if (options.decode) {
            typeof key === 'string' && (key = decodeURIComponent(key));
            typeof value === 'string' && (value = decodeURIComponent(value));
        }
        if (result[key] === undefined) {
            result[key] = value;
        }
        else if (options.parseArray) {
            result[key] = [].concat(result[key], value);
        }
    }
    return result;
}
/**
 * 序列化默认属性
 * @hidden
 */
export var DEFAULT_STRINGIFY_OPTIONS = {
    encode: true,
    skipEmptyString: false,
    addQuestionMark: false,
};
/**
 * 序列化 queryParam 对象
 *
 * ```js
 * FastUtils.stringifyQueryParam({ a: "1", b: "2" }); // => 'a=1&b=2'
 * FastUtils.stringifyQueryParam({ a: "1", b: ["2", "3"] }); // => 'a=1&b=2&b=3'
 * FastUtils.stringifyQueryParam({ a: "1", b: "2" }, { addQuestionMark: true }); // => '?a=1&b=2'
 *
 * // 是否跳过空字符串
 * FastUtils.stringifyQueryParam({ a: "", b: "" }); // => 'a=&b='
 * FastUtils.stringifyQueryParam({ a: "", b: "" }, { skipEmptyString: true }); // => ''
 *
 * // 其他情况
 * FastUtils.stringifyQueryParam({ a: null, b: undefined }); // => ''
 * ```
 *
 * @category UrlQuery
 * @param queryParamObj
 * @param options
 * - `encode`：默认为 `true`
 * - `skipEmptyString`：默认为 `false`
 * - `addQuestionMark`：默认为 `false`
 */
function stringifyQueryParam(queryParamObj, options) {
    var result = '';
    options = __assign(__assign({}, DEFAULT_STRINGIFY_OPTIONS), options);
    if (!queryParamObj) {
        return result;
    }
    var shouldFilter = function (key) {
        return queryParamObj[key] === null ||
            queryParamObj[key] === undefined ||
            (options.skipEmptyString && queryParamObj[key] === '');
    };
    var queryParamObjFilter = {};
    for (var _i = 0, _a = Object.keys(queryParamObj); _i < _a.length; _i++) {
        var key = _a[_i];
        if (!shouldFilter(key)) {
            queryParamObjFilter[key] = queryParamObj[key];
        }
    }
    result = Object.keys(queryParamObjFilter)
        .map(function (key) {
        var value = queryParamObjFilter[key];
        if (Array.isArray(value)) {
            return value
                .reduce(function (acc, value) {
                if (value === undefined ||
                    value === null ||
                    (options.skipEmptyString && value === '')) {
                    return acc;
                }
                if (options.encode) {
                    key = encodeURIComponent(key);
                    value = encodeURIComponent(value);
                }
                return __spreadArrays(acc, [[key, '=', value].join('')]);
            }, [])
                .join('&');
        }
        if (options.encode) {
            key = encodeURIComponent(key);
            value = encodeURIComponent(value);
        }
        return key + '=' + value;
    })
        .filter(function (x) { return x.length > 0; })
        .join('&');
    if (options.addQuestionMark && result.length > 0) {
        result = "?" + result;
    }
    return result;
}
/**
 * 获取 url 中 query 部分正则
 * @category UrlQuery
 * @hidden
 */
var QUERY_REGEX = /\?[^#]+/;
/**
 * 将 url 切分，便于操作
 * @category UrlQuery
 * @param url
 * @private
 */
function _splitUrl(url) {
    var result = { urlPart: '', queryPart: '', hashPart: '' };
    if (!url)
        return result;
    var urlArr = url.split('');
    var queryIndex = urlArr.indexOf('?');
    var hashIndex = urlArr.indexOf('#');
    if (hashIndex > -1) {
        result.hashPart = urlArr.splice(hashIndex).join('');
    }
    if (queryIndex > -1) {
        result.queryPart = urlArr.splice(queryIndex).join('');
    }
    result.urlPart = urlArr.join('');
    return result;
}
/**
 * 对外暴露 _splitUrl 方法
 *
 * ```js
 * FastUtils.splitUrl('http://127.0.0.1:8080/?query=1#hash'); // => {urlPart: "http://127.0.0.1:8080/", queryPart: "?query=1", hashPart: "#hash"}
 * ```
 * @category UrlQuery
 * @param url 默认为 `window.location.href`
 */
function splitUrl(url) {
    if (url === void 0) { url = window.location.href; }
    return _splitUrl(url);
}
/**
 * 获取 queryParam
 * @category UrlQuery
 * @param key
 * @param url
 * @param getArray
 * @private
 */
function _getQueryParam(key, url, getArray) {
    var queryParamObj = parseQueryParam(_splitUrl(url).queryPart, {
        parseArray: getArray,
    });
    return queryParamObj[key];
}
/**
 * 指定 key 获取 queryParam 的值
 *
 * ```js
 * FastUtils.getQueryParam('key', 'https://a.com?key=1'); // => '1'
 * FastUtils.getQueryParam('key', 'https://a.com?key='); // => ''
 * FastUtils.getQueryParam('key', 'https://a.com?key'); // => null
 * FastUtils.getQueryParam('key', 'https://a.com'); // => undefined
 *
 * // 遇到两个同名 key，只返回第一个值
 * FastUtils.getQueryParam('key', 'https://a.com?key=1&key=2'); // => '1'
 * ```
 *
 * @category UrlQuery
 * @param key
 * @param url 默认为 `window.location.href`
 */
function getQueryParam(key, url) {
    if (url === void 0) { url = window.location.href; }
    return _getQueryParam(key, url, false);
}
/**
 * 指定 key 获取 queryParam 的值
 *
 * 遇到多个同名 key 时会返回数组，其他和 [[getQueryParam]] 表现一致
 *
 * ```js
 * FastUtils.getQueryParams('key', 'https://a.com?key=1&key=2'); // => ['1', '2']
 * ```
 *
 * @category UrlQuery
 * @param key
 * @param url 默认为 `window.location.href`
 */
function getQueryParams(key, url) {
    if (url === void 0) { url = window.location.href; }
    return _getQueryParam(key, url, true);
}
/**
 * 指定 key 设置 queryParam 的值
 *
 * ```js
 * FastUtils.setQueryParam('key', '1', 'https://a.com'); // => "https://a.com?key=1"
 * FastUtils.setQueryParam('key', ['1', '2'], 'https://a.com'); // => "https://a.com?key=1&key=2"
 *
 * // 已有的同名 key 会被覆盖
 * FastUtils.setQueryParam('key', '2', 'https://a.com?key=1'); // => "https://a.com?key=2"
 *
 * // 其他情况
 * FastUtils.setQueryParam('key', '', 'https://a.com'); // => "https://a.com?key="
 * FastUtils.setQueryParam('key', null, 'https://a.com'); // => "https://a.com"
 * FastUtils.setQueryParam('key', undefined, 'https://a.com'); // => "https://a.com"
 * ```
 *
 * @category UrlQuery
 * @param key
 * @param value
 * @param url 默认为 `window.location.href`
 */
function setQueryParam(key, value, url) {
    if (url === void 0) { url = window.location.href; }
    var urlObj = _splitUrl(url);
    var queryParamObj = parseQueryParam(urlObj.queryPart, { parseArray: true });
    queryParamObj[key] = value;
    return "" + urlObj.urlPart + stringifyQueryParam(queryParamObj, {
        addQuestionMark: true,
    }) + urlObj.hashPart;
}
/**
 * 指定 key 移除 queryParam 的值
 *
 * ```js
 * FastUtils.removeQueryParam('key', 'https://a.com?key=1&key=2&other=3'); // => "https://a.com?other=3"
 * ```
 *
 * @category UrlQuery
 * @param key
 * @param url 默认为 `window.location.href`
 */
function removeQueryParam(key, url) {
    if (url === void 0) { url = window.location.href; }
    var urlObj = _splitUrl(url);
    var queryParamObj = parseQueryParam(urlObj.queryPart, { parseArray: true });
    delete queryParamObj[key];
    return "" + urlObj.urlPart + stringifyQueryParam(queryParamObj, {
        addQuestionMark: true,
    }) + urlObj.hashPart;
}
export { parseQueryParam, stringifyQueryParam, getQueryParam, getQueryParams, setQueryParam, removeQueryParam, splitUrl, };



//////////////////
// WEBPACK FOOTER
// ./node_modules/@ali/fast-utils/dist/url-query.js
// module id = null
// module chunks = 